/**
 * Diagnóstico e log ao vivo (spec 2026-09-24-robo-painel-de-ajustes, 4.1). Cada
 * dado diz de onde veio e há quanto tempo: pela USB é o que a placa respondeu
 * agora; pela rede, o `X-Robo-Diag` da última consulta. O log só existe pela
 * USB, de propósito, e já chega aqui redigido (`redacao.ts`).
 */
import { useState } from 'react'
import { useSeguirOFim } from '@/hooks/useSeguirOFim'
import { Button, Group, ScrollArea, Stack, Text } from '@mantine/core'
import { IconClipboard, IconPlayerPause, IconPlayerPlay, IconRefresh } from '@tabler/icons-react'

import type { DiagDaRede } from '@/api/robo'
import type { Diag, InfoDoRobo, Recursos } from '@/features/robo-usb/protocolo'
import { duracao } from './canal'
import { Linha } from './Linha'
import type { BufferDeLog, LinhaDoLog } from './redacao'

type Fonte = 'USB' | 'Rede' | 'Cadastro'
type Dado = { valor: string; fonte: Fonte; em: number | null } | null

const RESET: Record<string, string> = {
  poweron: 'ligado na energia',
  software: 'reinício pedido',
  panic: 'falha do firmware (panic)',
  watchdog: 'travou e o watchdog reiniciou',
  brownout: 'queda de tensão (brownout)',
  outro: 'outro motivo',
}

export function tempoLigado(segundos: number): string {
  const s = Math.max(0, Math.floor(segundos))
  const d = Math.floor(s / 86400)
  const h = Math.floor((s % 86400) / 3600)
  const m = Math.floor((s % 3600) / 60)
  if (d) return `${d} d ${h} h`
  if (h) return `${h} h ${m} min`
  if (m) return `${m} min ${s % 60} s`
  return `${s} s`
}

const kb = (bytes: number) => `${(bytes / 1024).toFixed(1).replace('.', ',')} KB`

function Valor({ dado, agora }: { dado: Dado; agora: number }) {
  if (!dado) return <Text size="sm" c="dimmed">Sem dado</Text>
  return (
    <Group gap="xs" wrap="wrap">
      <Text size="sm" ff="monospace">{dado.valor}</Text>
      <Text size="xs" c="dimmed">
        {dado.fonte}{dado.em !== null ? `, há ${duracao((agora - dado.em) / 1000)}` : ''}
      </Text>
    </Group>
  )
}

export type DiagnosticoDaUsb = {
  info: InfoDoRobo
  diag: Diag | null
  diagEm: number | null
  recursos: (Recursos & { em: number }) | null
}

export function SecaoDiagnostico({
  usb, rede, redeLidaEm, cadastro, log, versaoDoLog, agora, ocupado, aoLerDeNovo,
}: {
  usb: DiagnosticoDaUsb | null
  rede: DiagDaRede | null
  /** Quando o robô mandou o `X-Robo-Diag` (o `idade_s` descontado do momento da consulta). */
  redeLidaEm: number | null
  cadastro: { firmware_version: string | null; mac?: string | null }
  log: BufferDeLog
  versaoDoLog: number
  agora: number
  ocupado: boolean
  aoLerDeNovo: () => void
}) {
  const d = usb?.diag ?? null
  const r = usb?.recursos ?? null
  const deUsb = (valor: string, em: number | null): Dado => ({ valor, fonte: 'USB', em })
  const daRede = (valor: string): Dado => ({ valor, fonte: 'Rede', em: redeLidaEm })

  const firmware: Dado = usb
    ? deUsb(`${usb.info.versao} · ${usb.info.perfil}`, null)
    : cadastro.firmware_version ? { valor: cadastro.firmware_version, fonte: 'Cadastro', em: null } : null
  const mac: Dado = usb
    ? deUsb(usb.info.mac, null)
    : cadastro.mac ? { valor: cadastro.mac, fonte: 'Cadastro', em: null } : null
  const heap: Dado = r ? deUsb(kb(r.heap), r.em) : d ? deUsb(kb(d.heap), usb!.diagEm)
    : rede?.heap !== undefined ? daRede(kb(rede.heap)) : null
  const pilha: Dado = r ? deUsb(`${r.pilhaRede} bytes`, r.em) : d ? deUsb(`${d.pilha} bytes`, usb!.diagEm)
    : rede?.pilha !== undefined ? daRede(`${rede.pilha} bytes`) : null
  const rssi: Dado = d?.rssi !== undefined ? deUsb(`${d.rssi} dBm`, usb!.diagEm)
    : !usb && rede?.rssi !== undefined ? daRede(`${rede.rssi} dBm`) : null
  const up: Dado = d ? deUsb(tempoLigado(d.uptime_s), usb!.diagEm)
    : !usb && rede?.up !== undefined ? daRede(tempoLigado(rede.up)) : null
  const reset = d?.reset ?? (!usb ? rede?.reset : undefined)
  const resetDado: Dado = reset === undefined ? null
    : d ? deUsb(reset, usb!.diagEm) : daRede(reset)

  return (
    <Stack gap="md">
      <Stack gap="sm">
        <Linha rotulo="Firmware"><Valor dado={firmware} agora={agora} /></Linha>
        {usb && <Linha rotulo="Partição em uso"><Valor dado={deUsb(usb.info.app || '—', null)} agora={agora} /></Linha>}
        <Linha rotulo="Placa (MAC)"><Valor dado={mac} agora={agora} /></Linha>
        <Linha rotulo="Memória livre"><Valor dado={heap} agora={agora} /></Linha>
        <Linha rotulo="Pilha mínima da rede"><Valor dado={pilha} agora={agora} /></Linha>
        <Linha rotulo="Sinal Wi-Fi"><Valor dado={rssi} agora={agora} /></Linha>
        {usb && (
          <Linha rotulo="Rede em uso">
            <Valor dado={d?.ssid ? deUsb(d.ssid, usb.diagEm) : null} agora={agora} />
          </Linha>
        )}
        <Linha rotulo="Tempo ligado"><Valor dado={up} agora={agora} /></Linha>
        <Linha rotulo="Último reinício">
          <Valor dado={resetDado} agora={agora} />
          {reset !== undefined && <Text size="xs" c="dimmed">{RESET[reset] ?? reset}</Text>}
        </Linha>
      </Stack>
      <Group gap="sm">
        <Button variant="default" leftSection={<IconRefresh size={16} />} onClick={aoLerDeNovo} disabled={ocupado}>
          Ler de novo
        </Button>
        {!usb && (
          <Text size="xs" c="dimmed">Pela rede, o robô manda estes dados a cada consulta; o Console guarda o último por 15 min.</Text>
        )}
      </Group>

      <Stack gap="xs">
        <Text size="sm" fw={600}>Log ao vivo</Text>
        {usb
          ? <LogAoVivo log={log} versaoDoLog={versaoDoLog} />
          : <Text size="sm" c="dimmed">O log ao vivo só existe pela USB: conecte o robô neste computador.</Text>}
      </Stack>
    </Stack>
  )
}

function LogAoVivo({ log, versaoDoLog }: { log: BufferDeLog; versaoDoLog: number }) {
  const [congelado, setCongelado] = useState<readonly LinhaDoLog[] | null>(null)
  const [copiado, setCopiado] = useState(false)
  const linhas = congelado ?? log.todas()
  // Linha nova rola para o fim sem puxar quem rolou para cima (consoleai#25060);
  // pausado, o conteúdo é a foto congelada e não muda.
  const viewport = useSeguirOFim<HTMLDivElement>(congelado ?? versaoDoLog)

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(log.texto())
      setCopiado(true)
      setTimeout(() => setCopiado(false), 2_000)
    } catch {
      setCopiado(false)
    }
  }

  return (
    <Stack gap="xs">
      <Group gap="sm">
        <Button variant="default" size="xs" leftSection={<IconClipboard size={14} />} onClick={copiar}>
          {copiado ? 'Copiado' : 'Copiar'}
        </Button>
        <Button
          variant="default" size="xs"
          leftSection={congelado ? <IconPlayerPlay size={14} /> : <IconPlayerPause size={14} />}
          onClick={() => setCongelado(congelado ? null : [...log.todas()])}
        >
          {congelado ? 'Continuar' : 'Pausar'}
        </Button>
        <Text size="xs" c="dimmed">
          Até 2.000 linhas, só nesta aba. Linhas com token, senha ou credencial aparecem como [linha omitida].
        </Text>
      </Group>
      <ScrollArea h={260} viewportRef={viewport} type="auto"
        style={{ border: '1px solid var(--ai-border-light)', borderRadius: 'var(--mantine-radius-sm)' }}>
        <Stack gap={0} p="xs" data-testid="log-ao-vivo">
          {linhas.length === 0 && <Text size="xs" c="dimmed">Nenhuma linha ainda.</Text>}
          {linhas.map((l, i) => (
            <Text key={i} size="xs" ff="monospace" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
              {l.texto}
            </Text>
          ))}
        </Stack>
      </ScrollArea>
    </Stack>
  )
}
